/**
 * resolveLaunchTerm — the policy-membership + effective-date guard chain,
 * plus the chosen-term resolution, run BEFORE a launch payload is assembled.
 *
 * Extracted verbatim (same helpers, same order, same early-return-on-first-
 * error semantics) from the add-in's `InstanceDetailDrawer.handleLaunch`:
 *
 *   1. `buildLaunchBinding` + `splitLaunchValues` + `mergeLaunchSlice` — the
 *      merged READ view every helper below needs, because after `LaunchForm`
 *      normalisation the term identity lives on the ACTION while
 *      `policy_options` / `policy_term_key` stay at run level (binding is
 *      passed in already-built; the caller owns `buildLaunchBinding` since
 *      both `resolveLaunchTerm` and `assembleLaunchInputs` need it and
 *      building it twice per launch would be wasted work, not a behaviour
 *      difference).
 *   2. `validatePolicyMembership` — a typed/selected policy number must be
 *      one of the client's resolved options when the server resolved any;
 *      free text stays allowed otherwise.
 *   3. `validateEffectiveDateAgainstPolicy` — the effective date must fall
 *      inside the chosen term's window.
 *   4. `findPolicyOption` — the term the operator actually picked, used both
 *      for the blank-date fallback and the launch metadata downstream.
 *
 * ⚠️ Every guard here reads through `flat`, never the caller's raw `values`.
 * Reading `values` directly after a reshape would fail OPEN, not loudly: the
 * policy-membership backstop becomes a no-op, `validateEffectiveDateAgainstPolicy`
 * never fires, and `chosenTerm` resolves to `null` — silently re-opening the
 * future-term regression (instances 3b35db7f / e036c04d, 2026-08-05) this
 * module exists to keep closed on every host that calls it.
 *
 * On the FIRST guard that fails, this returns immediately with `chosenTerm:
 * null` and the guard's message in `error` — it does not go on to resolve a
 * term for a launch the caller is about to refuse. Mirrors `handleLaunch`'s
 * `setSubmitError(...); return;` early exits exactly.
 *
 * Pure: no React, no Office.js, no network.
 */
import type { PolicyInForce } from "~/common/workflows/types/client";
import {
  findPolicyOption,
  readPolicyOptions,
  readPolicyTermKey,
  validateEffectiveDateAgainstPolicy,
  validatePolicyMembership,
} from "~/common/workflows/launch/policyPrefillOptions";
import {
  mergeLaunchSlice,
  splitLaunchValues,
  type LaunchBinding,
} from "~/common/workflows/launch/launchSchemaBinding";

export interface ResolveLaunchTermResult {
  /** The merged READ view (`mergeLaunchSlice`) — the ONE record every guard
   *  and every downstream helper reads. Never spread back into the action. */
  flat: Record<string, unknown>;
  policyOptions: PolicyInForce[];
  /** `flat.policy_number`, trimmed; `""` when absent/non-string. */
  typedPolicy: string;
  /** The operator's picked term row, or `null` when nothing resolves (no
   *  options, no match, or a guard above already failed). */
  chosenTerm: PolicyInForce | null;
  /** The first guard's user-facing message, or `null` when both passed. */
  error: string | null;
}

/**
 * Resolve the guard chain and the chosen term for one launch attempt.
 *
 * `binding` is the caller's own `buildLaunchBinding(catalogEntry.input_schema
 * ?? null)` — build it once per launch and hand it to both this function and
 * `assembleLaunchInputs`, exactly as `handleLaunch` does today.
 */
export function resolveLaunchTerm(
  binding: LaunchBinding,
  values: Record<string, unknown> | null | undefined,
): ResolveLaunchTermResult {
  const { runLevelValues, actions } = splitLaunchValues(binding, values);
  const flat = mergeLaunchSlice(binding, runLevelValues, actions[0]);
  const policyOptions = readPolicyOptions(flat);
  const typedPolicy =
    typeof flat.policy_number === "string" ? flat.policy_number.trim() : "";
  const termKey = readPolicyTermKey(flat);

  const membershipError = validatePolicyMembership(policyOptions, typedPolicy);
  if (membershipError) {
    return {
      flat,
      policyOptions,
      typedPolicy,
      chosenTerm: null,
      error: membershipError,
    };
  }

  const effectiveDateError = validateEffectiveDateAgainstPolicy(
    flat.effective_date,
    policyOptions,
    typedPolicy || null,
    termKey,
  );
  if (effectiveDateError) {
    return {
      flat,
      policyOptions,
      typedPolicy,
      chosenTerm: null,
      error: effectiveDateError,
    };
  }

  const chosenTerm = findPolicyOption(policyOptions, typedPolicy || null, termKey);
  return { flat, policyOptions, typedPolicy, chosenTerm, error: null };
}
